/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'
import { defineMessages } from 'react-intl'

import {
  EuiBadge,
  EuiButtonEmpty,
  EuiFlexGroup,
  EuiFlexItem,
  EuiSpacer,
  EuiSkeletonText,
  useEuiBackgroundColor,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { CuiAlert } from '@modules/cui/Alert'
import { useGetSaasUserOktaGroups, usePostSaasUserOktaGroups } from '@modules/okta-lib'

type Props = {
  userId: string
}

const messages = defineMessages({
  addToSupportGroup: {
    id: 'user-okta-form.change',
    defaultMessage: 'Add to Support group',
  },
  addToBadUsersGroup: {
    id: 'user-okta-form.change-bad-user',
    defaultMessage: 'Add to Bad Users Group',
  },
})

const OktaGroups: React.FC<Props> = ({ userId }) => {
  const {
    data: oktaGroupsResponse,
    isLoading,
    error: fetchError,
  } = useGetSaasUserOktaGroups(userId)
  const postOktaGroupsMutation = usePostSaasUserOktaGroups()

  const primaryColor = useEuiBackgroundColor('primary')
  const dangerColor = useEuiBackgroundColor('danger')
  const subduedColor = useEuiBackgroundColor('subdued')

  if (isLoading) {
    return <EuiSkeletonText data-test-id='okta-groups-loading' lines={1} />
  }

  const groups = oktaGroupsResponse?.groups || []
  const groupNames = groups.map(({ name }) => name).sort()
  const supportIndex = groupNames.findIndex((g) => g === 'Support')
  const badUsersGroup = groupNames.findIndex((g) => g === 'BadUsersGroup')

  const getColor = (index: number) => {
    if (index === supportIndex) {
      return primaryColor
    }

    if (index === badUsersGroup) {
      return dangerColor
    }

    return subduedColor
  }

  const renderGroupBadge = (name: string, index: number) => (
    <EuiFlexItem key={index} grow={false}>
      <EuiBadge color={getColor(index)}>{name}</EuiBadge>
    </EuiFlexItem>
  )

  return (
    <Fragment>
      <EuiFlexGroup gutterSize='xs' alignItems='center' responsive={false} wrap={true}>
        {groupNames.map((group, index) => renderGroupBadge(group, index))}
        {supportIndex === -1 ? (
          <EuiFlexItem grow={false}>
            <EuiButtonEmpty
              size='xs'
              onClick={() =>
                postOktaGroupsMutation.mutate({
                  userId,
                  groupName: 'Support',
                })
              }
              isLoading={postOktaGroupsMutation.isLoading}
            >
              <FormattedMessage {...messages.addToSupportGroup} />
            </EuiButtonEmpty>
          </EuiFlexItem>
        ) : null}
        {badUsersGroup === -1 ? (
          <EuiFlexItem grow={false}>
            <EuiButtonEmpty
              size='xs'
              onClick={() =>
                postOktaGroupsMutation.mutate({
                  userId,
                  groupName: 'BadUsersGroup',
                })
              }
              isLoading={postOktaGroupsMutation.isLoading}
            >
              <FormattedMessage {...messages.addToBadUsersGroup} />
            </EuiButtonEmpty>
          </EuiFlexItem>
        ) : null}
      </EuiFlexGroup>

      {(fetchError || postOktaGroupsMutation.error) && (
        <Fragment>
          <EuiSpacer size='m' />
          <CuiAlert data-test-id='okta-groups-error' type='error'>
            {fetchError?.errors[0]?.message ||
              (postOktaGroupsMutation.error as any)?.errors?.[0]?.message ||
              'An error occurred while managing Okta groups'}
          </CuiAlert>
        </Fragment>
      )}
    </Fragment>
  )
}

export default OktaGroups
